import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  QUEUE_PHASE,
  abortQueueItemSession,
  applyUploadOutcomeToItems,
  buildReplacedPreviewItem,
  canRetryPrepare,
  createPreviewUrlForFile,
  createPropertyPrepareLock,
  createQueueItem,
  createPreviewRevokeRegistry,
  drainPropertyPrepareQueue,
  removeQueueItem,
  resetItemForPrepareRetry,
  resetItemForUploadRetry,
  revokePreviewUrl,
  takeItemsByLocalIds,
} from "./propertyMediaQueue";

function maxLocalId(items) {
  if (!items?.length) return 0;
  return items.reduce((max, it) => Math.max(max, it.localId), 0);
}

/**
 * Single Property media queue owner for create/edit surfaces.
 * One combined ordered collection; HEIC prepare concurrency = 1 per screen.
 *
 * Resource ownership rule: never allocate/revoke ObjectURLs or abort sessions
 * inside functional setState updaters (React.StrictMode may replay them).
 *
 * Mount lifecycle: every effect setup establishes a live owner. Cleanup only
 * finalizes abort/revoke after a microtask if the owner did not remount
 * (StrictMode setup→cleanup→setup must not leave mountedRef false or revoke
 * still-owned React state previews).
 */
export function usePropertyMediaQueue({
  items,
  setItems,
  enabled = true,
  prepareDeps = {},
}) {
  const activeRunRef = useRef(new Map());
  const drainingRef = useRef(false);
  const itemsRef = useRef(items);
  const enabledRef = useRef(enabled);
  const mountedRef = useRef(false);
  const ownerGenerationRef = useRef(0);
  const localIdSeqRef = useRef(maxLocalId(items));
  const prepareLockRef = useRef(null);
  const revokedUrlsRef = useRef(createPreviewRevokeRegistry());
  const ownedUrlsRef = useRef(new Set());

  if (!prepareLockRef.current) {
    prepareLockRef.current = createPropertyPrepareLock();
  }

  itemsRef.current = items;
  enabledRef.current = enabled;

  const safeSetItems = useCallback(
    (updater) => {
      if (!mountedRef.current) return;
      setItems(updater);
    },
    [setItems],
  );

  const isOwnerActive = useCallback(() => {
    return mountedRef.current && enabledRef.current;
  }, []);

  const registerOwnedPreview = useCallback((url) => {
    if (url) ownedUrlsRef.current.add(url);
  }, []);

  const revokeOwnedPreview = useCallback((itemOrUrl) => {
    const url =
      itemOrUrl && typeof itemOrUrl === "object"
        ? itemOrUrl.previewUrl
        : itemOrUrl;
    if (!url) return false;
    const revoked = revokePreviewUrl(url, revokedUrlsRef.current);
    if (revoked) ownedUrlsRef.current.delete(url);
    return revoked;
  }, []);

  /**
   * Exactly-once owner cleanup for one item:
   * invalidate runId, abort session once, revoke preview once, drop row.
   */
  const removeItem = useCallback(
    (localId) => {
      activeRunRef.current.delete(localId);
      const live = itemsRef.current.find((it) => it.localId === localId);
      if (live) {
        abortQueueItemSession(live);
        revokeOwnedPreview(live);
      }
      if (
        live
        && live.heicLike
        && (live.phase === QUEUE_PHASE.queued
          || live.phase === QUEUE_PHASE.checking
          || live.phase === QUEUE_PHASE.server_preview)
      ) {
        prepareLockRef.current?.cancelAllWaiters();
      }
      safeSetItems((prev) => removeQueueItem(prev, localId));
    },
    [revokeOwnedPreview, safeSetItems],
  );

  const invalidateItem = useCallback((localId) => {
    activeRunRef.current.delete(localId);
    const live = itemsRef.current.find((it) => it.localId === localId);
    if (live) abortQueueItemSession(live);
    prepareLockRef.current?.cancelAllWaiters();
  }, []);

  const patchItem = useCallback(
    (localId, patch) => {
      if (!mountedRef.current) return;
      const live = itemsRef.current.find((it) => it.localId === localId);
      if (!live) return;

      const computed =
        typeof patch === "function" ? patch(live) : { ...live, ...patch };
      const nextRow = { ...computed };
      const oldUrl = live.previewUrl;
      const newUrl = nextRow.previewUrl;
      const shouldRevokeOld = Boolean(oldUrl) && oldUrl !== newUrl;

      safeSetItems((prev) =>
        prev.map((it) => (it.localId === localId ? { ...it, ...nextRow } : it)),
      );

      if (shouldRevokeOld) {
        revokeOwnedPreview(oldUrl);
      }
    },
    [revokeOwnedPreview, safeSetItems],
  );

  const setActiveRun = useCallback((localId, runId) => {
    if (!mountedRef.current) return;
    activeRunRef.current.set(localId, runId);
  }, []);

  const allocateLocalIds = useCallback((count) => {
    const ids = [];
    for (let i = 0; i < count; i += 1) {
      localIdSeqRef.current += 1;
      ids.push(localIdSeqRef.current);
    }
    return ids;
  }, []);

  const addFiles = useCallback(
    (files, kind, options = {}) => {
      if (!isOwnerActive()) return;
      const ids = allocateLocalIds(files.length);
      const added = files.map((file, idx) =>
        createQueueItem(file, ids[idx], kind, options),
      );
      added.forEach((it) => registerOwnedPreview(it.previewUrl));
      safeSetItems((prev) => [...prev, ...added]);
    },
    [allocateLocalIds, isOwnerActive, registerOwnedPreview, safeSetItems],
  );

  const applyEditedFile = useCallback(
    (localId, file) => {
      if (!isOwnerActive()) return;
      const live = itemsRef.current.find((it) => it.localId === localId);
      if (!live) return;
      const oldUrl = live.previewUrl;
      const previewUrl = createPreviewUrlForFile(file);
      registerOwnedPreview(previewUrl);
      const next = buildReplacedPreviewItem(live, file, previewUrl);
      safeSetItems((prev) =>
        prev.map((it) => (it.localId === localId ? next : it)),
      );
      if (oldUrl) revokeOwnedPreview(oldUrl);
    },
    [isOwnerActive, registerOwnedPreview, revokeOwnedPreview, safeSetItems],
  );

  const applyUploadOutcome = useCallback(
    ({ succeededIds = [], failures = [] }) => {
      const succeededRows = takeItemsByLocalIds(
        itemsRef.current,
        succeededIds,
      );
      safeSetItems((prev) =>
        applyUploadOutcomeToItems(prev, succeededIds, failures),
      );
      succeededRows.forEach((row) => revokeOwnedPreview(row));
      return succeededRows;
    },
    [revokeOwnedPreview, safeSetItems],
  );

  const clearQueue = useCallback(() => {
    const snapshot = itemsRef.current.slice();
    snapshot.forEach((it) => {
      abortQueueItemSession(it);
      revokeOwnedPreview(it);
    });
    activeRunRef.current.clear();
    prepareLockRef.current?.cancelAllWaiters();
    safeSetItems(() => []);
  }, [revokeOwnedPreview, safeSetItems]);

  const retryPrepare = useCallback(
    (localId) => {
      if (!isOwnerActive()) return;
      const live = itemsRef.current.find((it) => it.localId === localId);
      if (!live || !canRetryPrepare(live)) return;
      activeRunRef.current.delete(localId);
      abortQueueItemSession(live);
      const next = resetItemForPrepareRetry(live, { skipAbort: true });
      safeSetItems((prev) =>
        prev.map((it) => (it.localId === localId ? next : it)),
      );
    },
    [isOwnerActive, safeSetItems],
  );

  const retryUpload = useCallback(
    (localId) => {
      if (!isOwnerActive()) return;
      const live = itemsRef.current.find((it) => it.localId === localId);
      if (!live) return;
      const next = resetItemForUploadRetry(live);
      safeSetItems((prev) =>
        prev.map((it) => (it.localId === localId ? next : it)),
      );
    },
    [isOwnerActive, safeSetItems],
  );

  const isCurrentRun = useCallback((localId, runId) => {
    if (!isOwnerActive()) return false;
    if (!itemsRef.current.some((it) => it.localId === localId)) return false;
    return activeRunRef.current.get(localId) === runId;
  }, [isOwnerActive]);

  const runDrain = useCallback(async () => {
    if (drainingRef.current || !isOwnerActive()) return;
    const generationAtStart = ownerGenerationRef.current;
    drainingRef.current = true;
    try {
      await drainPropertyPrepareQueue(itemsRef.current, {
        lock: prepareLockRef.current,
        shouldStop: () =>
          !isOwnerActive()
          || ownerGenerationRef.current !== generationAtStart,
        getItem: (localId) =>
          itemsRef.current.find((it) => it.localId === localId),
        isCurrent: isCurrentRun,
        isAllowed: () =>
          isOwnerActive() && ownerGenerationRef.current === generationAtStart,
        onStartRun: setActiveRun,
        onPatch: patchItem,
        ...prepareDeps,
      });
    } finally {
      if (ownerGenerationRef.current === generationAtStart) {
        drainingRef.current = false;
      }
    }
  }, [isCurrentRun, isOwnerActive, patchItem, prepareDeps, setActiveRun]);

  useEffect(() => {
    mountedRef.current = true;
    enabledRef.current = enabled;

    return () => {
      mountedRef.current = false;
      ownerGenerationRef.current += 1;
      prepareLockRef.current?.cancelAllWaiters();
      prepareLockRef.current?.bumpGeneration();
      activeRunRef.current.clear();
      drainingRef.current = false;

      const snapshot = itemsRef.current.slice();
      const ownedSnapshot = Array.from(ownedUrlsRef.current);

      queueMicrotask(() => {
        if (mountedRef.current) {
          // StrictMode remounted — keep React-owned previews; do not abort/revoke.
          return;
        }
        snapshot.forEach((it) => {
          abortQueueItemSession(it);
        });
        ownedSnapshot.forEach((url) => {
          revokeOwnedPreview(url);
        });
        snapshot.forEach((it) => {
          if (it?.previewUrl && !ownedSnapshot.includes(it.previewUrl)) {
            revokeOwnedPreview(it);
          }
        });
      });
    };
  }, [enabled, revokeOwnedPreview]);

  useEffect(() => {
    if (!enabled || !mountedRef.current) return undefined;
    const needsWork = items.some(
      (it) => it.heicLike && it.phase === QUEUE_PHASE.queued,
    );
    if (!needsWork) return undefined;
    let cancelled = false;
    (async () => {
      if (cancelled || !mountedRef.current) return;
      await runDrain();
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, items, runDrain]);

  const photos = useMemo(
    () => items.filter((it) => it.kind === "image"),
    [items],
  );
  const plans = useMemo(
    () => items.filter((it) => it.kind === "plan"),
    [items],
  );

  return {
    items,
    photos,
    plans,
    addFiles,
    removeItem,
    applyEditedFile,
    applyUploadOutcome,
    clearQueue,
    retryPrepare,
    retryUpload,
    patchItem,
    invalidateItem,
    runDrain,
    allocateLocalIds,
    // test/helper surface
    _revokedUrls: revokedUrlsRef,
    _ownedUrls: ownedUrlsRef,
    _mountedRef: mountedRef,
  };
}

export default usePropertyMediaQueue;
